import { useNavigate } from '@tanstack/react-router'
import { Send } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import {
  useCreatePurchaseOrder,
  useOrderingStatus,
} from '../hooks/use-purchase-orders'
import { baseLinesFromOrders } from '../lib/purchase-order-lines'
import { purchaseOrderErrorMessage } from '../lib/purchase-order-errors'
import { Button } from '@/components/ui/button'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

/**
 * "Bestellung senden" in the Bestellvorschlag (CHG-2026-10-edi-purchase-orders, D12): turns every
 * row with a Bestellung above 0 into a draft for the Liefertag (the first day of a range) and
 * opens it for review. Shown only with `purchase_order:order` and a Standort that is set up to
 * order; the report keeps its Bestellung.
 */
export function SendPurchaseOrderButton({
  restaurantId,
  deliveryDate,
  orders,
}: {
  restaurantId: number
  deliveryDate: string
  orders: Record<string, string>
}) {
  const { t } = useTranslation('purchase-orders')
  const navigate = useNavigate()
  const { can } = usePermissions()
  const allowed = can(ACTIONS.PURCHASE_ORDER_ORDER)
  const ordering = useOrderingStatus(restaurantId, allowed)
  const create = useCreatePurchaseOrder(restaurantId)

  if (!allowed || !ordering.data?.enabled || !ordering.data.configured)
    return null

  const lines = baseLinesFromOrders(orders)

  const submit = () =>
    create.mutate(
      { deliveryDate, lines },
      {
        onSuccess: (order) =>
          navigate({
            to: '/inventory/purchase-orders/$id',
            params: { id: order.id },
            search: { restaurantId },
          }),
        onError: (error) => toast.error(purchaseOrderErrorMessage(error, t)),
      },
    )

  return (
    <Button
      className="h-12 sm:h-9"
      onClick={submit}
      disabled={lines.length === 0 || create.isPending}
      title={lines.length === 0 ? t('proposal.noRows') : undefined}
    >
      <Send className="mr-1 h-4 w-4" aria-hidden="true" />
      {t('proposal.send')}
    </Button>
  )
}
